import type { TFunction } from "i18next";
import { AlertCircle, Minus, Plus, SearchX } from "lucide-react";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";

import {
  VACANCY_LIST,
  VACANCY_OVERRIDE,
  type VacancyListKind,
} from "../constants";
import { Collapse } from "./Collapse";
import { DelayedPlaceholder } from "./DelayedPlaceholder";
import { OverrideBadges } from "./OverrideBadges";
import { VacancyOverrideCard } from "./VacancyOverrideCard";
import { useLeavingItems } from "../hooks/useLeavingItems";
import { findOverride, isRedundantOverride } from "../overrides";

import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import type {
  SegmentVacancyItem,
  VacancyOverride,
} from "@/features/segments/types";
import { useLoadMoreSentinel } from "@/lib/hooks/useLoadMoreSentinel";
import { cn } from "@/lib/utils/cn";

const SKELETON_COUNT = 5;

type VacancyListProps = {
  list: VacancyListKind;
  items: SegmentVacancyItem[];
  overrides: VacancyOverride[];
  readOnly: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  hasQuery: boolean;
  isIncomplete: boolean;
  onFlip: (item: SegmentVacancyItem) => void;
  onRemoveOverride: (item: SegmentVacancyItem) => void;
  onOpen: (item: SegmentVacancyItem) => void;
  firstItemRef?: Ref<HTMLDivElement>;
  firstFlipRef?: Ref<HTMLButtonElement>;
};

export function VacancyList({
  list,
  items,
  overrides,
  readOnly,
  isLoading,
  isFetching,
  isError,
  hasMore,
  isLoadingMore,
  onLoadMore,
  hasQuery,
  isIncomplete,
  onFlip,
  onRemoveOverride,
  onOpen,
  firstItemRef,
  firstFlipRef,
}: VacancyListProps) {
  const { t } = useTranslation();
  const sentinelRef = useLoadMoreSentinel({
    hasMore,
    isLoadingMore,
    onLoadMore,
    rootMargin: "200px",
  });
  const { isLeaving, leave } = useLeavingItems();

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>
          {t("admin.segments.builder.vacancies.loadError")}
        </AlertDescription>
      </Alert>
    );
  }

  if (isIncomplete) {
    return (
      <EmptyState
        icon={SearchX}
        title={t("admin.segments.builder.vacancies.empty.incomplete")}
        layout="inline"
      />
    );
  }

  if (isLoading) {
    return (
      <DelayedPlaceholder>
        <ul className="flex flex-col gap-4">
          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <li key={index}>
              <Skeleton className="h-[88px] rounded-lg" />
            </li>
          ))}
        </ul>
      </DelayedPlaceholder>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title={getEmptyTitle(t, list, hasQuery)}
        description={
          hasQuery
            ? t("admin.segments.builder.vacancies.empty.queryDescription")
            : undefined
        }
        layout="inline"
      />
    );
  }

  return (
    <div
      aria-busy={isFetching}
      className={cn(
        "flex flex-col gap-4 transition-opacity duration-300 ease-out",
        isFetching && !isLoadingMore && "opacity-75",
      )}
    >
      <ul className="flex flex-col gap-4" aria-label={t("common.vacancies")}>
        {items.map((item, index) => (
          <Collapse
            key={item.vacancy.vacancyId}
            as="li"
            spacing={4}
            open={!isLeaving(item.vacancy.vacancyId)}
          >
            <VacancyListItem
              item={item}
              list={list}
              override={findOverride(overrides, item.vacancy.vacancyId)}
              readOnly={readOnly}
              onFlip={() => leave([item.vacancy.vacancyId], () => onFlip(item))}
              onRemoveOverride={() => onRemoveOverride(item)}
              onOpen={() => onOpen(item)}
              rootRef={index === 0 ? firstItemRef : undefined}
              flipRef={index === 0 ? firstFlipRef : undefined}
            />
          </Collapse>
        ))}
        {isLoadingMore && (
          <li>
            <Skeleton className="h-[88px] rounded-lg" />
          </li>
        )}
      </ul>
      <div ref={sentinelRef} aria-hidden />
    </div>
  );
}

const getEmptyTitle = (
  t: TFunction,
  list: VacancyListKind,
  hasQuery: boolean,
) => {
  if (hasQuery) return t("admin.segments.builder.vacancies.empty.query");
  if (list === VACANCY_LIST.INCLUDED)
    return t("admin.segments.builder.vacancies.empty.included");
  return t("admin.segments.builder.vacancies.empty.excluded");
};

type VacancyListItemProps = {
  item: SegmentVacancyItem;
  list: VacancyListKind;
  override: VacancyOverride["type"] | undefined;
  readOnly: boolean;
  onFlip: () => void;
  onRemoveOverride: () => void;
  onOpen: () => void;
  rootRef?: Ref<HTMLDivElement>;
  flipRef?: Ref<HTMLButtonElement>;
};

function VacancyListItem({
  item,
  list,
  override,
  readOnly,
  onFlip,
  onRemoveOverride,
  onOpen,
  rootRef,
  flipRef,
}: VacancyListItemProps) {
  const { t } = useTranslation();
  const isRedundant =
    override !== undefined && isRedundantOverride(override, item.matchesRules);
  const isIncludedList = list === VACANCY_LIST.INCLUDED;
  const flipLabel = getFlipLabel(t, isIncludedList, override, isRedundant);
  const FlipIcon = isIncludedList ? Minus : Plus;

  const action = !readOnly && (
    <div className="flex items-center gap-2">
      {isRedundant && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemoveOverride}
        >
          {t("admin.segments.builder.exceptions.removeException")}
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={flipRef}
            type="button"
            variant="ghost"
            size="icon"
            aria-label={flipLabel}
            onClick={onFlip}
            data-testid="vacancy-flip"
          >
            <FlipIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{flipLabel}</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <VacancyOverrideCard
      vacancy={item.vacancy}
      badges={
        override && <OverrideBadges type={override} isRedundant={isRedundant} />
      }
      action={action}
      onOpen={onOpen}
      rootRef={rootRef}
    />
  );
}

const getFlipLabel = (
  t: TFunction,
  isIncludedList: boolean,
  override: VacancyOverride["type"] | undefined,
  isRedundant: boolean,
) => {
  if (isIncludedList) {
    if (override === VACANCY_OVERRIDE.INCLUDE && !isRedundant)
      return t("admin.segments.builder.exceptions.removeInclude");
    return t("admin.segments.builder.exceptions.excludeManually");
  }
  if (override === VACANCY_OVERRIDE.EXCLUDE && !isRedundant)
    return t("admin.segments.builder.exceptions.removeExclude");
  return t("admin.segments.builder.exceptions.includeManually");
};
